/* Times Contrarian embed.

   The paper is an external WordPress site framed here. Message contract:

   inbound (paper -> app, only accepted when e.origin === TC_ORIGIN)
     { type: "tc-embed-ready" }
         the frame is listening; we answer with the member token.
     { type: "tc-nav", url, title }
         sent on every navigation inside the paper AND when the reader clicks
         an NYA link inside it. `url` tells the two apart — see classifyNavUrl.

   outbound (app -> paper, always posted to TC_ORIGIN)
     { type: "tc-embed-auth", idToken }
         member handoff, for the browsers that block framed silent SSO.
     { type: "nya-share-url", canonical, url }
         after each in-paper navigation: the paper's own url and the app url
         that reopens it, so the paper's Share row can offer the app link.

   History: navigations inside an iframe already create browser session-history
   entries, so Back works on its own and the frame owns the back stack. We
   therefore only ever `replace` the app url to keep the address bar in step,
   never `push` — pushing would add a second entry per article and take two
   Back presses to move one article. For the same reason a POP is ignored
   entirely: the browser has already restored both the frame and the url. */

import React, { useEffect, useRef, useState } from "react";
import { TC_ORIGIN, tcEmbedUrl } from "./tc-embed-config";
import {
  appPathToTcPath,
  classifyNavUrl,
  normalizeNyaPath,
  tcPathToAppPath,
} from "./tc-url";

// Diagnostics for staging: add ?tcdebug=1 to the url, or run
// localStorage.setItem("tc:debug", "1"), to log how each inbound message was
// classified and what the app did with it.
const TC_DEBUG =
  typeof window !== "undefined" &&
  (/[?&]tcdebug/.test(window.location.search) ||
    localStorage.getItem("tc:debug") === "1");

const debug = (...args) => {
  if (TC_DEBUG) console.log("[tc-embed]", ...args);
};

export default function TcEmbed({ history, location }) {
  const frameRef = useRef(null);


  const framedPathRef = useRef(
    location ? location.pathname : "/news"
  );

  const [frame, setFrame] = useState(() => ({
    src: tcEmbedUrl(appPathToTcPath(location ? location.pathname : "/news")),
    nonce: 0,
  }));

  // The last path we wrote ourselves, so the effect below can tell our own
  // url sync from a genuine navigation somewhere else in the app.
  const selfNavRef = useRef(null);

  // Latest props for the message handler, which is registered once.
  const routerRef = useRef({ history, location });
  routerRef.current = { history, location };

  useEffect(() => {
    const handleMessage = (data, source) => {
      const frame = frameRef.current;

      if (data.type === "tc-embed-ready") {
        const idToken = localStorage.getItem("ade:id-token");
        if (idToken && frame && frame.contentWindow === source) {
          frame.contentWindow.postMessage(
            { type: "tc-embed-auth", idToken },
            TC_ORIGIN
          );
        }
        return;
      }

      if (data.type === "tc-nav") {
        if (typeof data.url !== "string") {
          debug("tc-nav ignored, url is not a string", data);
          return;
        }

        const { kind, path } = classifyNavUrl(data.url);
        debug("tc-nav", {
          url: data.url,
          kind,
          path,
          appPath: kind === "tc" ? tcPathToAppPath(path) : null,
          currentPath: routerRef.current.location.pathname,
        });

        if (kind === "nya") {
          // The reader clicked out of the paper. Push, so Back returns them to
          // the article. TcEmbed unmounts and the frame goes with it.
          const target = normalizeNyaPath(path);
          debug("routing app to", target);
          routerRef.current.history.push(target);
          return;
        }

        if (kind !== "tc") return;

        window.__tcCanonicalUrl = data.url;
        window.__tcCanonicalTitle =
          typeof data.title === "string" ? data.title : undefined;

        const appPath = tcPathToAppPath(path);
        const current = routerRef.current.location;
        if (appPath !== `${current.pathname}${current.search}`) {
          debug("syncing url", `${current.pathname}${current.search}`, "->", appPath);
          selfNavRef.current = appPath;
          framedPathRef.current = appPath;
          routerRef.current.history.replace(appPath);
        } else {
          debug("url already in sync, no replace", appPath);
        }

        if (frame && frame.contentWindow) {
          frame.contentWindow.postMessage(
            {
              type: "nya-share-url",
              canonical: data.url,
              url: `${window.location.origin}${appPath}`,
            },
            TC_ORIGIN
          );
        }
      }
    };

    const onMessage = (e) => {
      if (e.origin !== TC_ORIGIN) return;
      const data = e.data;
      if (!data || typeof data !== "object") return;
      handleMessage(data, e.source);
    };

    window.addEventListener("message", onMessage);

    // Staging aid: feed the handler a message as if the paper had sent it, so
    // the app's half can be exercised before the paper's bridge is emitting.
    //   __tcSimulateNav("https://neilyoungarchives.com/movie-night/x")
    if (TC_DEBUG) {
      window.__tcSimulateNav = (url, title) => {
        debug("SIMULATED tc-nav", url);
        handleMessage(
          { type: "tc-nav", url, title },
          frameRef.current && frameRef.current.contentWindow
        );
      };
    }

    return () => {
      window.removeEventListener("message", onMessage);
      if (TC_DEBUG) delete window.__tcSimulateNav;
    };
  }, []);


  useEffect(() => {
    if (!location) return;
    if (history.action === "POP") return;
    if (location.pathname === selfNavRef.current) {

      selfNavRef.current = null;
      return;
    }
    if (location.pathname === framedPathRef.current) return;

    framedPathRef.current = location.pathname;
    const src = tcEmbedUrl(appPathToTcPath(location.pathname));
    setFrame((prev) => ({ src, nonce: prev.nonce + 1 }));
  }, [location && location.key]);

  return (
    <div className="newspaper-page">
      <div id="newspaper">
        <iframe
          ref={frameRef}
          key={`${frame.nonce}:${frame.src}`}
          src={frame.src}
          title="Times Contrarian"
          style={{ border: 0, width: "100%", height: "100%" }}
          allow="fullscreen; clipboard-write; encrypted-media; picture-in-picture"
          allowFullScreen
        />
      </div>
    </div>
  );
}
